"use client";

import React, { useEffect, useMemo, useRef } from "react";

export type Category = {
  _id: string;
  title: string;
  slug?: string;
};

interface CategorySelect2Props {
  categories?: Category[] | null;

  name?: string;
  label?: string;
  placeholder?: string;
  multiple?: boolean;

  value?: string[];
  disableSearch?: boolean;
  allowClear?: boolean;
  className?: string;

  onChange: (payload: { name: string; value: string | string[] }) => void;
}

const CategorySelect2: React.FC<CategorySelect2Props> = ({
  categories = [], // ✅ default empty array
  name = "category_ids",
  label = "Categories",
  placeholder = "Select category",
  multiple = true,
  value,
  disableSearch = false,
  allowClear = true,
  className = "form-select",
  onChange,
}) => {
  const selectRef = useRef<HTMLSelectElement | null>(null);

  const options = useMemo(() => {
    return (categories ?? []).map((c) => ({
      label: c.title,
      value: c._id,
    }));
  }, [categories]);

  useEffect(() => {
    const $ = (window as any).$;
    if (!$ || !selectRef.current) return;

    const $select = $(selectRef.current);

    if (typeof $select.select2 !== "function") {
      console.error("Select2 is not available. Make sure select2 is loaded.");
      return;
    }

    $select.select2({
      placeholder,
      allowClear,
      minimumResultsForSearch: disableSearch ? -1 : 0,
      width: "100%",
    });

    // ✅ set initial value
    if (value !== undefined) {
      $select.val(value).trigger("change.select2");
    }

    const handleChange = () => {
      const selected = $select.val();

      if (multiple) {
        onChange({ name, value: (selected as string[]) || [] });
      } else {
        onChange({ name, value: (selected as string) || "" });
      }
    };

    $select.on("change", handleChange);

    return () => {
      $select.off("change", handleChange);
      $select.select2("destroy");
    };
  }, [name, placeholder, allowClear, disableSearch, multiple, onChange]);

  // ✅ sync value updates
  useEffect(() => {
    const $ = (window as any).$;
    if (!$ || !selectRef.current) return;

    const $select = $(selectRef.current);
    if (typeof $select.select2 !== "function") return;

    if (value !== undefined) {
      $select.val(value).trigger("change.select2");
    }
  }, [value]);

  return (
   
      <select
        ref={selectRef}
        name={name}
        className={className}
        multiple={multiple}
        defaultValue={multiple ? [] : ""}
        disabled={(categories ?? []).length === 0} // ✅ optional
      >
        {!multiple && <option value="">{placeholder}</option>}

        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    
  );
};

export default CategorySelect2;
